/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {AgGridReact} from 'ag-grid-react';
import classNames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {dlsColor} from '../theme/theme';
import IonGridHeader from './IonGridHeader';
import {HEADER_HEIGHT, ROW_HEIGHT} from './IonGridUtils';
import {IonSpinnerTemplate} from './IonSpinnerTemplate';
import PaginationControls from './PaginationControls';
import {useGridEventListener} from './useGridEventListener';
import {useGridPageSize} from './useGridPageSize';

interface BasicGridProps {
  rowData: any[];
  customWidth?: number;
  columnDefs: any[];
  defaultColDef?: any;
  gridHeight?: string;
  gridWidth?: string;
  enableHeader?: boolean;
  customGridClass?: string;
  rowModelType?: string;
  disablePortal?: boolean;
  customPaperClass?: string;
  showLoadingOverlay?: boolean;
  onDelete?: (assets: any) => void;
  onGridReady?: (params: any) => void;
  preference?: string;
}

export enum RowModelTypes {
  ClientSide = 'clientSide'
}

const useStyles = makeStyles((theme) => ({
  defaultGridHeight : {
    '& .ag-row-hover.ag-row-hover, .ag-row-hover, .ag-row-selected.ag-row-selected, .ag-row-selected, .ag-row-focus.ag-row-focus, .ag-row-focus' : {
      backgroundColor : `${dlsColor('paleBlue')} !important`,
      backgroundImage : 'none !important',
      '&::before'     : {
        backgroundColor : `${dlsColor('paleBlue')} !important`,
        backgroundImage : 'none !important'
      }
    },
    '& .ag-ltr' : {
      '& .ag-cell-focus:not(.ag-cell-range-selected):focus-within' : {
        border      : 'none !important',
        borderColor : '#d2d2d2 !important'
      }
    },
    '& .ag-cell-value' : {
      display : 'flex !important'
    },
    '& .ag-header-select-all' : {
      marginLeft : 1
    },
    height : '100%',
    width  : '100%'
  },
  pagination : {
    [theme.breakpoints.down('sm')] : {
      height          : '104px',
      backgroundColor : dlsColor('white')
    }
  },
  header : {
    position : 'absolute',
    top      : '-47px',
    width    : ({customWidth}: {customWidth: number}) => customWidth
  }
}));

/**
 * The grid component which renders basic react grid with custom pagination and header components.
 */
const BasicGrid = ({
  rowData,
  onDelete,
  columnDefs,
  customWidth,
  onGridReady,
  customGridClass,
  customPaperClass,
  preference,
  disablePortal = false,
  showLoadingOverlay = true
}: BasicGridProps) => {
  const classes                               = useStyles({customWidth});
  const [gridApi, setGridApi]                 = useState(null);
  const [selectedRows, setSelectedRows]       = useState<Array<any>>([]);
  const [paginationState, setPaginationState] = useState(null);

  const [pageSize, setPageSize] = useGridPageSize({
    usePreferedPageSize : false, defaultPageSize : 20
  });
  
  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('paginationPageSize', pageSize);
    }
  }, [gridApi, pageSize]);

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption('rowData', rowData);
    }
  }, [gridApi, rowData]);

  const handleGridReady = function(params: any) {
    setGridApi(params.api);
    if (onGridReady) {
      onGridReady(params);
    }
  };

  const onPaginationChange = useCallback(({api}) => {
    const selections = api?.getSelectedRows();
    setSelectedRows(selections);
  }, []);

  function onSelectionChanged(api) {
    onPaginationChange(api);
  }

  const handlePaginationChanged = useCallback(function(params) {
    setPaginationState(params);
  }, []);

  useGridEventListener(gridApi, 'selectionChanged', onSelectionChanged);
  useGridEventListener(gridApi, 'paginationChanged', onPaginationChange);

  const getHeader = () => {
    return (
      <IonGridHeader allowSingleDelete
        hasHeader={gridApi}
        count={selectedRows?.length}
        selectedRows={selectedRows}
        onBulkDelete={onDelete}
        gridApi={gridApi} />
    );
  };

  return (
    <div className={classNames('ag-theme-quartz', classes.defaultGridHeight, customGridClass)}>
      <div className={classes.header}>{!!(selectedRows?.length) && getHeader()}</div>
      <AgGridReact animateRows
        suppressMenuHide
        suppressPaginationPanel
        overlayNoRowsTemplate={translate('grids:noResults')}
        onPaginationChanged={handlePaginationChanged}
        columnDefs={columnDefs}
        overlayLoadingTemplate={showLoadingOverlay ? IonSpinnerTemplate : '<div></div>'}
        pagination={true}
        gridOptions={{
          onSelectionChanged,
          singleClickEdit           : true,
          rowSelection              : 'multiple',
          suppressRowClickSelection : true
        }}
        paginationPageSize={pageSize}
        rowModelType={RowModelTypes.ClientSide}
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        onGridReady={handleGridReady} />
      <div className={classes.pagination}>
        <PaginationControls gridApi={gridApi}
          paginationState={paginationState}
          preference={preference}
          setPageSize={setPageSize}
          disablePortal={disablePortal}
          customPaperClass={customPaperClass}
          pageSize={pageSize}
          pageSizes={PAGE_SIZES}
          totalResultCount={rowData?.length || 0} />
      </div>
    </div>
  );
};
export default BasicGrid;
